'use client';
import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import utilsService from '../helpers/utilsService';
import { NotificationService } from '../service/notifications';
import { isAppBridge } from '../helpers/appStorageMethod';
import chatService from '../service/chatService';
import { updateAppBadge } from '../helpers/badgeHelper';

// Define the shape of the context
interface PostMessageContextValue {
    isAllowedNotifications: boolean | null;
    sendPermissionRequest: (isAllowed: boolean) => Promise<boolean>;
}

// Create context
const PostMessageContext = createContext<PostMessageContextValue | undefined>(undefined);

// Provider component
export const PostMessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const router = useRouter();
    let permissionResolver: ((value: boolean) => void) | null = null;
    const [isAllowedNotifications, setIsAllowedNotifications] = useState<boolean | null>(null);
    const lastProcessedMessageRef = useRef<{ id: string; time: number } | null>(null);

    // Handler for postMessage
    const handlePostMessage = async (event: any) => {
        try {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Deduplicate postMessage events triggered multiple times (e.g. window + document on Android)
            const messageKey = data?.event_id || data?.message_id || JSON.stringify(data);
            const now = Date.now();
            if (lastProcessedMessageRef.current &&
                lastProcessedMessageRef.current.id === messageKey &&
                now - lastProcessedMessageRef.current.time < 2000) {
                console.log('[AppListenerEventPostMessage] Duplicate message detected, skipping processing');
                return;
            }
            lastProcessedMessageRef.current = { id: messageKey, time: now };

            if (data?.IS_ALLOW_NOTIFICATIONS_WEBVIEW !== undefined) {
                setIsAllowedNotifications(data.IS_ALLOW_NOTIFICATIONS_WEBVIEW);
                if (permissionResolver) {
                    permissionResolver(data.IS_ALLOW_NOTIFICATIONS_WEBVIEW);
                    permissionResolver = null; // Reset resolver
                }
            }
            if (data?.MESSAGE_TYPE === 'notification') {
                const link = data.link;
                const notiType = data.type as string | undefined;
                const eventId = data.event_id ? Number(data.event_id) : undefined;
                const badgeCount = data.badge_count ? Number(data.badge_count) : undefined;
                const conversationId = data.conversation_id ? Number(data.conversation_id) : undefined;


                if (link) {
                    gotoLink(link, notiType, eventId, conversationId, badgeCount);
                } else {
                    router.push('/mychannel/notifications');
                }
            }

            // event_id 가 없는 알림(RN 이 'notification-signal' 로 보낸다 — 관리자 방송 이벤트,
            // 마이룸 3D 아이템 완료 등). 앱은 일부러 이 타입엔 읽음 처리를 안 시킨다 — event_id
            // 없이 읽음 API 를 부르면 서버가 그 유저의 안 읽은 알림 전체를 읽음 처리한다
            // (NotificationsComponent/MyChannelService::readNotification 마지막 분기).
            // 그래서 여기서도 이동만 하고 읽음 처리는 절대 부르지 않는다.
            if (data?.MESSAGE_TYPE === 'notification-signal') {
                const link = data.link;
                if (link && utilsService.isUrl(link)) {
                    const parsedUrl = new URL(link);
                    if (utilsService.normalizeDomain(parsedUrl.hostname) === utilsService.getAppDomain()) {
                        router.push(parsedUrl.pathname + parsedUrl.search);
                    }
                }
            }
        } catch (error) {
            console.error('Error handling postMessage:', error);
        }
    };

    const sendPermissionRequest = useCallback((): Promise<boolean> => {
        return new Promise((resolve) => {
            if (window.ReactNativeWebView) {
                permissionResolver = resolve;
                window.ReactNativeWebView.postMessage('IS_ALLOW_NOTIFICATIONS_WEBVIEW');
            } else {
                resolve(false);
            }
        });
    }, []);

    function gotoLink(url: string, notiType?: string, eventId?: number, conversationId?: number, badgeCount?: number) {
        if (url && utilsService.isUrl(url)) {
            const parsedUrl = new URL(url);
            const domain = utilsService.normalizeDomain(parsedUrl.hostname);
            const currentDomain = utilsService.getAppDomain();


            if (domain == currentDomain) {

                const updateBadge = () => {
                    updateAppBadge();
                };

                // 읽음 처리가 실패해도 사용자에게 알리지 않는다.
                //
                // 전에는 여기서 [DEBUG] alert 을 띄웠는데, 이 두 곳만 isDebugUser 가드
                // 밖에 있어 **전 사용자에게** 떴다. 알림을 누를 때마다 도는 호출이라
                // 서버가 잠깐 흔들리거나 네트워크가 끊기면 일반 사용자가 영문 모를
                // 디버그 문구를 봤다(2026-09-19 확인).
                //
                // 읽음 표시는 실패해도 사용자가 할 일이 없다 — 다음에 다시 눌리면 된다.
                // 조사는 콘솔로 남긴다.
                const onReadFailed = (err: any) => {
                    // eslint-disable-next-line no-console
                    console.warn('[noti] 읽음 처리 실패', err?.message || err);
                };

                if (notiType === 'chat-message' && conversationId) {
                    // DM: mark conversation as read
                    chatService.markConversationAsRead(conversationId)
                        .then(updateBadge)
                        .catch(onReadFailed);
                } else {
                    // Regular noti: mark đúng noti đó theo event_id
                    NotificationService.readNotifications(eventId)
                        .then(updateBadge)
                        .catch(onReadFailed);
                }

                // U8/E1: 쿼리스트링(예: 열람 추적용 ?apd=)을 버리면 안 된다 — pathname 만
                // 쓰면 알림 링크에 실어 보낸 파라미터가 라우팅 직전에 사라진다(조사로 발견).
                router.push(parsedUrl.pathname + parsedUrl.search);
            } else {
                // External URLs are blocked - uncomment below to allow opening in new tab
                // window.open(url, '_blank');
                return;
            }
        } else {
        }
    }

    // Add listener for postMessage
    useEffect(() => {
        if (window.ReactNativeWebView) {
            window.addEventListener('message', handlePostMessage);
            document.addEventListener('message', handlePostMessage); // Android
            window.ReactNativeWebView.postMessage('IS_LISTENING_READY');
        }

        return () => {
            if (window.ReactNativeWebView) {
                window.removeEventListener('message', handlePostMessage);
                document.removeEventListener('message', handlePostMessage); // Android
            }
            // }
        };
    }, []);

    return <PostMessageContext.Provider value={{ isAllowedNotifications, sendPermissionRequest }}>{children}</PostMessageContext.Provider>;
};

// Hook for consuming the context
export const usePostMessage = () => {
    const context = useContext(PostMessageContext);
    if (!context) {
        throw new Error('usePostMessage must be used within a PostMessageProvider');
    }
    return context;
};
